import axios from 'axios'
import firebase from 'firebase/compat/app'
import { showError } from './toast'

/**
 * Uploads an image to the backend and returns the mediaObjectId or URL
 * @param {File} image - The image file to upload
 * @param {boolean} [returnUrl=false] - Whether to return the full URL instead of the ID
 * @param {Object} toast - The toast instance
 * @param {Object} t - The t instance
 * @param {string} [moderationMode='async'] - 'sync' for immediate blocking, 'async' for background moderation
 * @returns {Promise<string|null>} The mediaObjectId or URL, or null
 */
export async function uploadImage(
    image,
    returnUrl = false,
    toast,
    t,
    moderationMode = 'async'
) {
    const apiUrl = import.meta.env.VITE_API_URL
    const uploadUrl = `${apiUrl}media/upload-image`
    console.log('uploading image', image)

    // Send image to backend endpoint as binary buffer with correct mime-type
    try {
        const idtoken = await firebase.auth().currentUser?.getIdToken(true)

        // Get the correct mime-type from the image file
        const contentType = image.type
        const imageBuffer = await image.arrayBuffer()

        // Only add idtoken header if idtoken is set
        const headers = {
            'Content-Type': contentType,
            'x-moderation-mode': moderationMode, // Tell backend whether to block (sync) or allow (async)
        }
        if (idtoken) {
            headers.idtoken = idtoken
        }
        const response = await axios.post(uploadUrl, imageBuffer, {
            headers: headers,
        })

        //Treat response
        if (response.status >= 200 && response.status < 300) {
            console.log('Image uploaded successfully', response.data)
            return returnUrl ? response.data.url : response.data
        } else {
            console.log('Error uploading file', response)
            return null
        }
    } catch (error) {
        if (error.response && error.response.status === 451) {
            // CSAM detection
            const errorMessage = t(
                'errors.csam-detected',
                'This content cannot be uploaded due to legal restrictions.'
            )
            if (moderationMode === 'async') {
                // Async mode: show toast
                showError(toast, errorMessage)
            }
            console.error('CSAM detected during image upload')
            // Re-throw for sync mode to handle
            if (moderationMode === 'sync') {
                throw error
            }
            return null
        } else if (error.response && error.response.status === 403) {
            // Moderation rejection
            const moderationResult = error.response.data?.moderationResult
            if (moderationMode === 'async') {
                // Async mode: show toast
                if (moderationResult && moderationResult.violation) {
                    const errorMessage =
                        moderationResult.reason ||
                        t('errors.media-blocked', 'This media has been blocked')
                    showError(toast, errorMessage)
                } else {
                    showError(
                        toast,
                        t('errors.media-blocked', 'This media has been blocked')
                    )
                }
                console.log('Content blocked by moderation:', moderationResult)
                return null
            } else {
                // Sync mode: don't show toast, throw error for wrapper to handle
                console.log(
                    'Content blocked by moderation (sync mode):',
                    moderationResult
                )
                throw error
            }
        } else {
            console.log('Error uploading file', error)
            return null
        }
    }
}

/**
 * Uploads an audio file to the backend and returns the mediaObjectId
 * @param {Blob} audio - The audio file to upload
 * @param {number} duration - The duration of the audio file in seconds
 * @param {Object} toast - The toast instance
 * @param {Object} t - The t instance
 * @returns {Promise<string>} The mediaObjectId
 */
export async function uploadAudio(audio, duration, toast, t) {
    const apiUrl = import.meta.env.VITE_API_URL
    const uploadUrl = `${apiUrl}media/upload-audio`
    try {
        const idtoken = await firebase.auth().currentUser.getIdToken(true)

        const response = await axios.post(uploadUrl, audio, {
            headers: {
                'Content-Type': audio.type || 'audio/webm;codecs=opus',
                idtoken: idtoken,
                'x-duration': duration,
            },
        })

        if (response.status >= 200 && response.status < 300) {
            return response.data
        } else {
            console.log('Error uploading audio', response)
            return null
        }
    } catch (error) {
        if (error.response && error.response.status === 451) {
            // CSAM detection - show error for async mode
            const errorMessage = t(
                'errors.csam-detected',
                'This content cannot be uploaded due to legal restrictions.'
            )
            showError(toast, errorMessage)
            console.error('CSAM detected during audio upload')
        } else if (error.response && error.response.status === 403) {
            // Moderation rejection - show error for async mode
            showError(
                toast,
                t('errors.media-blocked', 'This media has been blocked')
            )
        } else {
            console.log('Error uploading audio', error)
        }
        return null
    }
}

/**
 * Uploads a video file to the backend and returns the mediaObjectId
 * @param {File} video - The video file to upload
 * @param {Object} toast - The toast instance
 * @param {Object} t - The t instance
 * @param {Object} options - Optional upload options
 * @param {number} options.maxDuration - Maximum duration in seconds (will trim video if longer)
 * @param {string} [moderationMode='async'] - 'sync' for immediate blocking, 'async' for background moderation
 * @returns {Promise<string>} The mediaObjectId
 */
export async function uploadVideo(
    video,
    toast,
    t,
    options = {},
    moderationMode = 'async'
) {
    const apiUrl = import.meta.env.VITE_API_URL
    const uploadUrl = `${apiUrl}media/upload-video`

    console.log('[uploadVideo] Starting video upload:', {
        fileName: video?.name,
        fileSize: video?.size,
        fileType: video?.type,
        options,
        uploadUrl,
    })

    try {
        console.log('[uploadVideo] Getting Firebase auth token...')
        const idtoken = await firebase.auth().currentUser.getIdToken(true)
        console.log('[uploadVideo] Auth token obtained')

        const formData = new FormData()
        formData.append('video', video)

        // Add maxDuration to FormData if provided
        if (options.maxDuration) {
            formData.append('maxDuration', options.maxDuration.toString())
            console.log(
                '[uploadVideo] Added maxDuration to FormData:',
                options.maxDuration
            )
        }
        if (options.preset) {
            formData.append('preset', options.preset)
            console.log(
                '[uploadVideo] Added preset to FormData:',
                options.preset
            )
        }
        if (options.generateThumbnail === false) {
            formData.append('generateThumbnail', 'false')
            console.log(
                '[uploadVideo] Disabled thumbnail generation via FormData'
            )
        }
        if (options.resolution) {
            formData.append('resolution', options.resolution)
            console.log(
                '[uploadVideo] Added resolution to FormData:',
                options.resolution
            )
        }
        if (options.fps) {
            formData.append('fps', options.fps)
            console.log('[uploadVideo] Added fps to FormData:', options.fps)
        }

        console.log('[uploadVideo] Sending POST request to backend...')
        const response = await axios.post(uploadUrl, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                idtoken: idtoken,
                'x-moderation-mode': moderationMode, // Tell backend whether to block (sync) or allow (async)
            },
        })

        console.log('[uploadVideo] Response received:', {
            status: response.status,
            statusText: response.statusText,
            data: response.data,
        })

        if (response.status >= 200 && response.status < 300) {
            console.log('[uploadVideo] Upload successful, returning data')
            return response.data
        } else {
            console.error('[uploadVideo] Unexpected response status:', response)
            return null
        }
    } catch (error) {
        console.error('[uploadVideo] Error occurred:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            stack: error.stack,
        })

        if (error.response && error.response.status === 451) {
            // CSAM detection
            const errorMessage = t(
                'errors.csam-detected',
                'This content cannot be uploaded due to legal restrictions.'
            )
            if (moderationMode === 'async') {
                showError(toast, errorMessage)
            }
            console.error('CSAM detected during video upload')
            if (moderationMode === 'sync') {
                throw error
            }
            return null
        } else if (error.response && error.response.status === 403) {
            // Moderation rejection
            const moderationResult = error.response.data?.moderationResult
            if (moderationMode === 'async') {
                // Async mode: show toast
                if (moderationResult && moderationResult.violation) {
                    const errorMessage =
                        moderationResult.reason ||
                        t('errors.media-blocked', 'This media has been blocked')
                    showError(toast, errorMessage)
                } else {
                    showError(
                        toast,
                        t('errors.media-blocked', 'This media has been blocked')
                    )
                }
                console.log('Content blocked by moderation:', moderationResult)
                return null
            } else {
                // Sync mode: don't show toast, throw error for wrapper to handle
                console.log(
                    'Content blocked by moderation (sync mode):',
                    moderationResult
                )
                throw error
            }
        } else {
            console.error('Error uploading video:', error)
            return null
        }
    }
}

export default {
    uploadImage,
    uploadAudio,
    uploadVideo,
}
